import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// Create a separate axios instance for public endpoints (no auth required)
const publicApiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

export interface PublicSalonResponse {
    id: string;
    slug: string;
    name: string;
    address: string | null;
    phone: string | null;
}

export interface PublicServiceResponse {
    id: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    price: number;
    category: string | null;
}

export interface PublicEmployeeResponse {
    id: string;
    fullName: string;
    role: string;
    photoUrl?: string;
}

export interface TimeSlot {
    time: string; // HH:mm format
    available: boolean;
}

export interface AvailabilityResponse {
    date: string;
    serviceId: string;
    employeeId: string | null;
    availableSlots: TimeSlot[];
}

export interface PublicBookingCreateRequest {
    employeeId: string;
    serviceId: string;
    clientName: string;
    clientPhone: string;
    clientEmail?: string;
    startTime: string; // ISO 8601 format
    notes?: string;
    verificationToken?: string;
    recaptchaToken?: string;
    vipCode?: string;
}

// Phone verification interfaces
export interface PhoneVerificationRequest {
    phone: string;
}

export interface PhoneVerificationConfirmRequest {
    phone: string;
    code: string;
}

export interface PhoneVerificationResponse {
    verified: boolean;
    token: string | null;
    message: string;
}

// Public-facing salon booking config (subset for the client)
export interface PublicBookingConfig {
    requirePhoneVerification: boolean;
    bookingMode: 'OPEN' | 'APPROVAL_REQUIRED' | 'PRIVATE';
    showPricing: boolean;
    showEmployeeFullNames: boolean;
    bookingWindowDays: number;
    vipModeEnabled: boolean;
}

export interface PublicBookingResponse {
    bookingId: string;
    salonName: string;
    serviceName: string;
    employeeName: string;
    clientName: string;
    startTime: string;
    endTime: string;
    status: string;
    confirmationMessage: string;
}

export const publicBookingApi = {
    /**
     * Get salon information by slug
     */
    getSalon: async (salonSlug: string): Promise<PublicSalonResponse> => {
        const response = await publicApiClient.get<PublicSalonResponse>(`/public/${salonSlug}`);
        return response.data;
    },

    /**
     * Get all services for a salon
     */
    getServices: async (salonSlug: string): Promise<PublicServiceResponse[]> => {
        const response = await publicApiClient.get<PublicServiceResponse[]>(`/public/${salonSlug}/services`);
        return response.data;
    },

    /**
     * Get all employees for a salon, optionally filtered by service
     */
    getEmployees: async (salonSlug: string, serviceId?: string): Promise<PublicEmployeeResponse[]> => {
        const params = serviceId ? { serviceId } : {};
        const response = await publicApiClient.get<PublicEmployeeResponse[]>(`/public/${salonSlug}/employees`, { params });
        return response.data;
    },

    /**
     * Get available time slots for a specific date and service
     */
    getAvailability: async (
        salonSlug: string,
        date: string,
        serviceId: string,
        employeeId?: string
    ): Promise<AvailabilityResponse> => {
        const params: Record<string, string> = { date, serviceId };
        if (employeeId) {
            params.employeeId = employeeId;
        }
        const response = await publicApiClient.get<AvailabilityResponse>(`/public/${salonSlug}/availability`, { params });
        return response.data;
    },

    /**
     * Create a public booking
     */
    createBooking: async (salonSlug: string, request: PublicBookingCreateRequest): Promise<PublicBookingResponse> => {
        const response = await publicApiClient.post<PublicBookingResponse>(`/public/${salonSlug}/booking`, request);
        return response.data;
    },

    /**
     * Get booking config for a salon (public-facing subset)
     */
    getBookingConfig: async (salonSlug: string): Promise<PublicBookingConfig> => {
        const response = await publicApiClient.get<PublicBookingConfig>(`/public/${salonSlug}/booking-config`);
        return response.data;
    },

    // ========== Phone Verification ==========

    /**
     * Send verification code to phone
     */
    sendVerificationCode: async (salonSlug: string, phone: string): Promise<void> => {
        await publicApiClient.post(`/public/${salonSlug}/verify/send`, { phone });
    },

    /**
     * Confirm verification code
     */
    confirmVerificationCode: async (salonSlug: string, phone: string, code: string): Promise<PhoneVerificationResponse> => {
        const response = await publicApiClient.post<PhoneVerificationResponse>(
            `/public/${salonSlug}/verify/confirm`,
            { phone, code }
        );
        return response.data;
    },

    /**
     * Verify VIP Access Code
     */
    verifyVipCode: async (salonSlug: string, code: string): Promise<{ valid: boolean; message: string }> => {
        const response = await publicApiClient.post<{ valid: boolean; message: string }>(
            `/public/${salonSlug}/verify-vip`,
            { code }
        );
        return response.data;
    },
};
